/*
 * Client-side ZIP assembly for product 3D download bundles.
 *
 * It copies complete local ZIP records and rewrites only the central
 * directory offsets. No member data is inflated or recompressed.
 */

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;
const ZIP64_EOCD_SIGNATURE = 0x06064b50;
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50;
const MAX_EOCD_SEARCH = 22 + 0xffff;
const UINT32_MAX = 0xffffffff;
const MAX_BUNDLE_FILES = 32;
const MAX_ARCHIVE_PART_BYTES = 20_000_000;
const MAX_BUNDLE_BYTES = 256 * 1024 * 1024;

function fail(message) {
  throw new Error(`Invalid ZIP archive: ${message}`);
}

function isUint8Array(value) {
  return value instanceof Uint8Array && !(value instanceof DataView);
}

function ensureRange(bytes, offset, length, label) {
  if (!Number.isSafeInteger(offset) || !Number.isSafeInteger(length) || offset < 0 || length < 0
    || offset + length > bytes.length) {
    fail(`${label} is truncated`);
  }
}

function readU16(view, offset) {
  return view.getUint16(offset, true);
}

function readU32(view, offset) {
  return view.getUint32(offset, true);
}

function writeU16(view, offset, value) {
  view.setUint16(offset, value, true);
}

function writeU32(view, offset, value) {
  view.setUint32(offset, value, true);
}

function signature(bytes, offset) {
  if (offset < 0 || offset + 4 > bytes.length) return 0;
  return new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0, true);
}

function decodeName(bytes, offset, length) {
  ensureRange(bytes, offset, length, 'filename');
  let name;
  try {
    name = new TextDecoder('utf-8', { fatal: true }).decode(bytes.subarray(offset, offset + length));
  } catch (_error) {
    fail('filename is not valid UTF-8');
  }
  if (!name || name.includes('\0') || name.includes('\\') || name.startsWith('/') || /^[A-Za-z]:/.test(name)
    || name.split('/').some((part) => part === '..' || part === '')) {
    fail(`unsafe filename ${JSON.stringify(name)}`);
  }
  if (name.endsWith('/')) fail(`directory filename ${JSON.stringify(name)}`);
  return name;
}

function parseExtraFields(bytes, offset, length, name) {
  ensureRange(bytes, offset, length, `${name} extra field`);
  const view = new DataView(bytes.buffer, bytes.byteOffset + offset, length);
  for (let cursor = 0; cursor < length;) {
    if (cursor + 4 > length) fail(`${name} extra field is truncated`);
    const fieldId = readU16(view, cursor);
    const fieldLength = readU16(view, cursor + 2);
    cursor += 4;
    if (cursor + fieldLength > length) fail(`${name} extra field is truncated`);
    if (fieldId === 0x0001) fail(`${name} uses ZIP64 extra fields`);
    cursor += fieldLength;
  }
}

function findEndOfCentralDirectory(bytes) {
  const start = Math.max(0, bytes.length - MAX_EOCD_SEARCH);
  for (let offset = bytes.length - 22; offset >= start; offset -= 1) {
    if (signature(bytes, offset) !== EOCD_SIGNATURE) continue;
    ensureRange(bytes, offset, 22, 'end-of-central-directory');
    const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 22);
    const commentLength = readU16(view, 20);
    ensureRange(bytes, offset, 22 + commentLength, 'end-of-central-directory comment');
    if (offset + 22 + commentLength !== bytes.length) continue;
    return { offset, view };
  }
  fail('end-of-central-directory record not found');
}

function parseArchive(input, archiveIndex) {
  if (!isUint8Array(input)) fail(`archive ${archiveIndex + 1} is not a Uint8Array`);
  const bytes = input;
  if (bytes.length < 22) fail(`archive ${archiveIndex + 1} is too small`);

  const eocd = findEndOfCentralDirectory(bytes);
  // ZIP64 records have fixed structural positions relative to the EOCD and
  // must not be detected by scanning compressed payload bytes for signatures.
  if (signature(bytes, eocd.offset - 20) === ZIP64_LOCATOR_SIGNATURE) fail(`archive ${archiveIndex + 1} uses ZIP64`);
  if (signature(bytes, eocd.offset - 56) === ZIP64_EOCD_SIGNATURE) fail(`archive ${archiveIndex + 1} uses ZIP64`);
  const view = eocd.view;
  const diskNumber = readU16(view, 4);
  const centralDisk = readU16(view, 6);
  const entriesOnDisk = readU16(view, 8);
  const entriesTotal = readU16(view, 10);
  const centralSize = readU32(view, 12);
  const centralOffset = readU32(view, 16);
  if (diskNumber !== 0 || centralDisk !== 0 || entriesOnDisk !== entriesTotal) {
    fail(`archive ${archiveIndex + 1} is multi-disk`);
  }
  if (entriesTotal === 0xffff || centralSize === UINT32_MAX || centralOffset === UINT32_MAX) {
    fail(`archive ${archiveIndex + 1} requires ZIP64`);
  }
  ensureRange(bytes, centralOffset, centralSize, 'central directory');
  if (centralOffset + centralSize !== eocd.offset) fail('central directory does not end at EOCD');

  const centralEntries = [];
  const localOffsets = new Set();
  const names = new Set();
  let cursor = centralOffset;
  for (let index = 0; index < entriesTotal; index += 1) {
    ensureRange(bytes, cursor, 46, `central directory entry ${index + 1}`);
    if (signature(bytes, cursor) !== CENTRAL_SIGNATURE) fail(`central directory entry ${index + 1} signature`);
    const centralView = new DataView(bytes.buffer, bytes.byteOffset + cursor, 46);
    const flags = readU16(centralView, 8);
    const method = readU16(centralView, 10);
    const crc32 = readU32(centralView, 16);
    const compressedSize = readU32(centralView, 20);
    const uncompressedSize = readU32(centralView, 24);
    const nameLength = readU16(centralView, 28);
    const extraLength = readU16(centralView, 30);
    const commentLength = readU16(centralView, 32);
    const diskStart = readU16(centralView, 34);
    const localOffset = readU32(centralView, 42);
    const entryLength = 46 + nameLength + extraLength + commentLength;
    ensureRange(bytes, cursor, entryLength, `central directory entry ${index + 1}`);
    if (flags & 1) fail(`encrypted member ${index + 1}`);
    if (flags & 0x08) fail(`data-descriptor member ${index + 1} is unsupported`);
    if (method !== 0 && method !== 8) fail(`unsupported compression method ${method}`);
    if (diskStart !== 0 || localOffset === UINT32_MAX || compressedSize === UINT32_MAX || uncompressedSize === UINT32_MAX) {
      fail(`central directory entry ${index + 1} requires ZIP64 or another disk`);
    }
    const name = decodeName(bytes, cursor + 46, nameLength);
    parseExtraFields(bytes, cursor + 46 + nameLength, extraLength, `central entry ${index + 1}`);
    if (names.has(name)) fail(`duplicate filename ${JSON.stringify(name)}`);
    if (localOffsets.has(localOffset)) fail(`duplicate local offset for ${JSON.stringify(name)}`);
    names.add(name);
    localOffsets.add(localOffset);
    centralEntries.push({
      name,
      flags,
      method,
      crc32,
      compressedSize,
      uncompressedSize,
      localOffset,
      centralOffset: cursor,
      centralLength: entryLength,
      centralBytes: bytes.subarray(cursor, cursor + entryLength)
    });
    cursor += entryLength;
  }
  if (cursor !== centralOffset + centralSize) fail('central directory entry count/size mismatch');

  const entries = centralEntries.map((entry, index) => {
    const localOffset = entry.localOffset;
    ensureRange(bytes, localOffset, 30, `local header for ${entry.name}`);
    if (signature(bytes, localOffset) !== LOCAL_SIGNATURE) fail(`local header signature for ${entry.name}`);
    const localView = new DataView(bytes.buffer, bytes.byteOffset + localOffset, 30);
    const localFlags = readU16(localView, 6);
    const localMethod = readU16(localView, 8);
    const localCrc32 = readU32(localView, 14);
    const localCompressedSize = readU32(localView, 18);
    const localUncompressedSize = readU32(localView, 22);
    const localNameLength = readU16(localView, 26);
    const localExtraLength = readU16(localView, 28);
    const localHeaderLength = 30 + localNameLength + localExtraLength;
    ensureRange(bytes, localOffset, localHeaderLength, `local header for ${entry.name}`);
    if (localFlags !== entry.flags || localMethod !== entry.method) fail(`local/central flags or method mismatch for ${entry.name}`);
    const localName = decodeName(bytes, localOffset + 30, localNameLength);
    if (localName !== entry.name) fail(`local/central filename mismatch for ${entry.name}`);
    parseExtraFields(bytes, localOffset + 30 + localNameLength, localExtraLength, `local entry ${index + 1}`);
    if (localCrc32 !== entry.crc32 || localCompressedSize !== entry.compressedSize || localUncompressedSize !== entry.uncompressedSize) {
      fail(`local/central size or CRC mismatch for ${entry.name}`);
    }
    const recordEnd = localOffset + localHeaderLength + entry.compressedSize;
    ensureRange(bytes, localOffset, localHeaderLength + entry.compressedSize, `compressed member ${entry.name}`);
    if (recordEnd > centralOffset) fail(`member ${entry.name} overlaps central directory`);
    return { ...entry, localLength: localHeaderLength + entry.compressedSize, localBytes: bytes.subarray(localOffset, recordEnd) };
  });

  // Source fixtures are standard contiguous archives. Requiring no hidden
  // bytes between local records and the central directory prevents copying
  // ambiguous data and catches truncated/malformed records deterministically.
  const sorted = [...entries].sort((a, b) => a.localOffset - b.localOffset);
  let expectedOffset = 0;
  for (const entry of sorted) {
    if (entry.localOffset !== expectedOffset) fail(`unexpected local-record gap before ${entry.name}`);
    expectedOffset = entry.localOffset + entry.localLength;
  }
  if (expectedOffset !== centralOffset) fail('local records do not end at central directory');
  return entries;
}

/**
 * Combine standard, non-encrypted ZIP archives without inflating members.
 * @param {Uint8Array[]} arrayOfUint8Arrays
 * @returns {Blob}
 */
export function combineZipArchives(arrayOfUint8Arrays) {
  if (!Array.isArray(arrayOfUint8Arrays) || arrayOfUint8Arrays.length === 0) {
    fail('at least one archive is required');
  }
  const allEntries = [];
  const names = new Set();
  for (let archiveIndex = 0; archiveIndex < arrayOfUint8Arrays.length; archiveIndex += 1) {
    const entries = parseArchive(arrayOfUint8Arrays[archiveIndex], archiveIndex);
    for (const entry of entries) {
      if (names.has(entry.name)) fail(`duplicate filename across archives ${JSON.stringify(entry.name)}`);
      names.add(entry.name);
      allEntries.push(entry);
    }
  }
  if (allEntries.length === 0 || allEntries.length > 0xffff) fail('invalid combined entry count');

  let localSize = 0;
  for (const entry of allEntries) {
    if (localSize > UINT32_MAX || entry.localBytes.length > UINT32_MAX - localSize) fail('combined archive exceeds ZIP32 limits');
    localSize += entry.localBytes.length;
  }
  let centralSize = 0;
  for (const entry of allEntries) {
    if (centralSize > UINT32_MAX || entry.centralBytes.length > UINT32_MAX - centralSize) fail('central directory exceeds ZIP32 limits');
    centralSize += entry.centralBytes.length;
  }
  if (localSize > UINT32_MAX - centralSize - 22) fail('combined archive exceeds ZIP32 limits');

  const localParts = [];
  const centralParts = [];
  let offset = 0;
  for (const entry of allEntries) {
    localParts.push(entry.localBytes);
    const central = new Uint8Array(entry.centralBytes);
    const centralView = new DataView(central.buffer, central.byteOffset, central.byteLength);
    writeU32(centralView, 42, offset);
    centralParts.push(central);
    offset += entry.localBytes.length;
  }
  const eocdBytes = new Uint8Array(22);
  const eocdView = new DataView(eocdBytes.buffer);
  writeU32(eocdView, 0, EOCD_SIGNATURE);
  writeU16(eocdView, 4, 0);
  writeU16(eocdView, 6, 0);
  writeU16(eocdView, 8, allEntries.length);
  writeU16(eocdView, 10, allEntries.length);
  writeU32(eocdView, 12, centralSize);
  writeU32(eocdView, 16, localSize);
  writeU16(eocdView, 20, 0);
  return new Blob([...localParts, ...centralParts, eocdBytes], { type: 'application/zip' });
}

function archivePartManifest(urls) {
  const partPattern = /^(.*)-archive-part-(\d{3})-of-(\d{3})-([a-f0-9]{64})(?:_[a-f0-9-]+)?\.bin$/i;
  const names = urls.map((url) => url.split(/[?#]/, 1)[0].split('/').pop());
  if (!names.some((name) => name.toLowerCase().includes('-archive-part-'))) return null;
  const parts = names.map((name) => {
    const match = partPattern.exec(name);
    if (!match) throw new Error('archive-parts-invalid');
    return { prefix: match[1], index: Number(match[2]), count: Number(match[3]), sha256: match[4].toLowerCase() };
  });
  if (parts.some((part, index) => part.index !== index + 1 || part.count !== urls.length || part.prefix !== parts[0].prefix)) {
    throw new Error('archive-parts-order-invalid');
  }
  return parts;
}

/** Assemble byte parts of one verified ZIP; each filename carries its SHA-256. */
export async function assembleZipArchiveParts(arrays, urls) {
  const manifest = archivePartManifest(urls);
  if (!manifest || arrays.length !== manifest.length || arrays.length > MAX_BUNDLE_FILES) throw new Error('archive-parts-invalid');
  if (arrays.reduce((total, bytes) => total + (bytes?.byteLength || 0), 0) > MAX_BUNDLE_BYTES) throw new Error('archive-total-size-invalid');
  for (let index = 0; index < arrays.length; index += 1) {
    const bytes = arrays[index];
    if (!isUint8Array(bytes) || bytes.length === 0 || bytes.length >= MAX_ARCHIVE_PART_BYTES) throw new Error('archive-part-size-invalid');
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
    if (sha256 !== manifest[index].sha256) throw new Error('archive-part-integrity-invalid');
  }
  if (signature(arrays[0], 0) !== LOCAL_SIGNATURE) throw new Error('archive-part-header-invalid');
  const last = arrays[arrays.length - 1];
  findEndOfCentralDirectory(last);
  return new Blob(arrays, { type: 'application/zip' });
}

async function fetchArchive(url, budget) {
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`archive-http-${response.status}`);
  const declaredSize = Number(response.headers?.get('content-length'));
  if (Number.isFinite(declaredSize) && declaredSize >= MAX_ARCHIVE_PART_BYTES) {
    await response.body?.cancel();
    throw new Error('archive-part-size-invalid');
  }
  function account(length) {
    budget.used += length;
    if (budget.used > MAX_BUNDLE_BYTES) throw new Error('archive-total-size-invalid');
  }
  if (!response.body?.getReader) {
    if (!Number.isFinite(declaredSize) || declaredSize <= 0) throw new Error('archive-stream-unavailable');
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (bytes.length === 0 || bytes.length >= MAX_ARCHIVE_PART_BYTES) throw new Error('archive-part-size-invalid');
    account(bytes.length);
    return bytes;
  }
  const reader = response.body.getReader();
  const chunks = [];
  let length = 0;
  try {
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      length += chunk.value.byteLength;
      if (length >= MAX_ARCHIVE_PART_BYTES) throw new Error('archive-part-size-invalid');
      account(chunk.value.byteLength);
      chunks.push(chunk.value);
    }
    if (length === 0) throw new Error('archive-part-size-invalid');
  } catch (error) {
    await reader.cancel().catch(() => {});
    throw error;
  } finally {
    reader.releaseLock();
  }
  const bytes = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}

async function fetchArchives(urls) {
  const results = new Array(urls.length);
  const budget = { used: 0 };
  let nextIndex = 0;
  async function worker() {
    while (true) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= urls.length) return;
      results[index] = await fetchArchive(urls[index], budget);
    }
  }
  const outcomes = await Promise.allSettled(Array.from({ length: Math.min(3, urls.length) }, worker));
  const failed = outcomes.find((outcome) => outcome.status === 'rejected');
  if (failed) throw failed.reason;
  return results;
}

function parseBundleFiles(button) {
  let urls;
  try {
    urls = JSON.parse(button.dataset.bundleFiles || 'null');
  } catch (_error) {
    throw new Error('bundle-files-invalid');
  }
  if (!Array.isArray(urls) || urls.length === 0 || urls.length > MAX_BUNDLE_FILES || urls.some((url) => typeof url !== 'string' || !url)) {
    throw new Error('bundle-files-invalid');
  }
  archivePartManifest(urls);
  return urls;
}

function bindBundleButton(button) {
  if (!button || button.dataset.merlons3dBundleBound === 'true') return;
  const container = button.closest('[data-merlons-3d-bundle-container]');
  if (!container) return;
  const label = button.querySelector('[data-bundle-label]');
  const status = container.querySelector('[data-bundle-status]');
  if (!label || !status) return;
  try {
    parseBundleFiles(button);
    if (typeof button.dataset.archiveName !== 'string' || !button.dataset.archiveName.trim()) return;
  } catch (_error) {
    return;
  }
  const readyLabel = label.textContent;
  button.dataset.merlons3dBundleBound = 'true';
  button.disabled = false;
  button.addEventListener('click', async () => {
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.hidden = false;
    status.textContent = button.dataset.preparingText || '';
    let objectUrl = null;
    try {
      const urls = parseBundleFiles(button);
      const arrays = await fetchArchives(urls);
      const blob = archivePartManifest(urls)
        ? await assembleZipArchiveParts(arrays, urls)
        : combineZipArchives(arrays);
      objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = button.dataset.archiveName || 'merlons-3d-models.zip';
      anchor.hidden = true;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      status.textContent = '';
      status.hidden = true;
    } catch (_error) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      status.textContent = button.dataset.errorText || '';
      status.hidden = false;
    } finally {
      label.textContent = readyLabel;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
}

export function initMerlons3dBundleDownloads(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  root.querySelectorAll('button[data-merlons-3d-bundle]').forEach(bindBundleButton);
}

if (typeof document !== 'undefined') {
  const initialize = () => initMerlons3dBundleDownloads(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initMerlons3dBundleDownloads(event.target || document));
}
